import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../useI18n.js';
import { DRAW_W, DRAW_H, getCanvas, applySegment, subscribe, copyDrawingToClipboard } from '../drawStore';

/**
 * Zone de dessin collaborative (partagee via socket). Le canvas maitre est dans
 * drawStore ; ce composant le miroite et capture le dessin local.
 */
export function DrawBoard({ onSegment, onClearVote, clearInfo }) {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const lastRef = useRef(null);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#111827');
  const [penSize, setPenSize] = useState(4);
  const [eraserSize, setEraserSize] = useState(28);
  const [copied, setCopied] = useState('');

  // Miroir du canvas maitre (mise a jour a chaque segment recu/dessine).
  useEffect(() => {
    const blit = () => {
      const c = canvasRef.current;
      if (!c) return;
      const g = c.getContext('2d');
      g.clearRect(0, 0, DRAW_W, DRAW_H);
      g.drawImage(getCanvas(), 0, 0);
    };
    blit();
    return subscribe(blit);
  }, []);

  const posFrom = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (DRAW_W / Math.max(1, r.width)),
      y: (e.clientY - r.top) * (DRAW_H / Math.max(1, r.height)),
    };
  };
  const curSize = () => (tool === 'eraser' ? eraserSize : penSize);
  const emit = (seg) => { applySegment(seg); if (onSegment) onSegment(seg); };

  const onDown = (e) => {
    const p = posFrom(e);
    lastRef.current = p;
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    emit({ x0: p.x, y0: p.y, x1: p.x, y1: p.y, tool, color, size: curSize() }); // point
  };
  const onMove = (e) => {
    if (!lastRef.current) return;
    const p = posFrom(e);
    emit({ x0: lastRef.current.x, y0: lastRef.current.y, x1: p.x, y1: p.y, tool, color, size: curSize() });
    lastRef.current = p;
  };
  const onUp = () => { lastRef.current = null; };

  const doExport = async () => {
    try { await copyDrawingToClipboard(); setCopied('ok'); }
    catch { setCopied('err'); }
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div className="drawboard">
      <div className="draw-tools">
        <button type="button" className={`draw-tool${tool === 'pen' ? ' active' : ''}`} onClick={() => setTool('pen')} title={t('drawPen') || 'Stylo'}>✏️</button>
        <button type="button" className={`draw-tool${tool === 'eraser' ? ' active' : ''}`} onClick={() => setTool('eraser')} title={t('drawEraser') || 'Gomme'}>🧽</button>
        {tool === 'pen' && (
          <label className="draw-color" title={t('drawColor') || 'Couleur'}>
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
          </label>
        )}
        <label className="draw-size">
          <input
            type="range" min={1} max={tool === 'eraser' ? 80 : 40} step={1}
            value={tool === 'eraser' ? eraserSize : penSize}
            onChange={(e) => (tool === 'eraser' ? setEraserSize : setPenSize)(parseInt(e.target.value, 10))}
          />
          <span className="draw-size-dot" style={{ width: curSize() / 2 + 4, height: curSize() / 2 + 4, background: tool === 'eraser' ? '#cbd5e1' : color }} />
        </label>
      </div>

      <canvas
        ref={canvasRef}
        width={DRAW_W}
        height={DRAW_H}
        className="draw-canvas"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      />

      <div className="draw-footer">
        <button type="button" className="draw-export" onClick={doExport}>
          {copied === 'ok' ? (t('drawCopied') || 'Copie !') : copied === 'err' ? (t('drawCopyErr') || 'Echec') : (t('drawExport') || 'Exporter')}
        </button>
        {onClearVote && (
          <button type="button" className="draw-clear" onClick={onClearVote} title={t('drawClearVoteHint') || 'Voter pour vider (51%)'}>
            🗑 {t('drawClearVote') || 'Vider'}{clearInfo && clearInfo.votes ? ` (${clearInfo.votes}/${clearInfo.needed})` : ''}
          </button>
        )}
      </div>
    </div>
  );
}
